import { UploadRounded } from "@mui/icons-material";
import { Box, Button, Stack, StackOwnProps, Typography } from "@mui/material";
import React, { useEffect, useRef } from "react";
import "@uppy/core/dist/style.min.css";
import { useAppDispatch, useTypedSelector } from "@/config/configureAppStore";
import DragDrop from "@uppy/drag-drop";
import "./index.css";
import uppyFactory from "@/features/createProject/uppyFactory";
import useCreateProject from "@/features/createProject/hooks/useCreateProject";
import useUppy from "@/features/createProject/uppyFactory/hooks/useUppy";
import {
  CREATE_PROJECT_STEPS,
  PROJECT_FLOW_TYPES
} from "@/features/createProject/constants";
import { setAddMoreFilesModal } from "@/reducers/slices/dialogSlice/dialogSlice";
import { MISC_COLORS } from "@/themes/themeVariables";
import accessibilityUtils from "@/utils/accessibility";
import {
  setCurrentStep,
  setProjectId
} from "@/reducers/slices/clientProjectPopupSlice/clientProjectPopupSlice";
import { UppyFile } from "@uppy/core";
import { useDesktopNotifications } from "@/features/notifications";

const blockStyles: StackOwnProps = {
  gap: 5,
  justifyContent: "center",
  alignItems: "center",
  flexGrow: 1,
  flexShrink: 1,
  p: 5,
  borderRadius: 4,
  position: "relative",
  sx: {
    width: "50%",
    minHeight: 280
  }
};

const PLUGIN_ID = "Custom File Drag n Drop";

export const enum UPLOAD_FILES_ENTRY_POINT {
  CREATE_PROJECT = "CREATE_PROJECT",
  PROJECT_DETAILS = "PROJECT_DETAILS"
}

export const UploadFiles = ({
  title = "Drag & drop your files here or",
  entryPoint
}: {
  title?: string;
  entryPoint: UPLOAD_FILES_ENTRY_POINT;
}): React.ReactNode => {
  const dispatch = useAppDispatch();
  const { projectId, selectedSampleFiles, sourceFileResponseList } =
    useTypedSelector((state) => state.clientProjectPopup);
  const uppyRef = useRef<HTMLDivElement>(null);
  const isCreatingProject = useRef(false);
  const isProjectCreated = useRef(false);
  const shouldUpload = useRef(false);

  const {
    handleCurrentStep,
    currentStep,
    isLoading,
    handleCreateProject,
    setIsLoading,
    currentFlowType,
    handleSampleFileUpload
  } = useCreateProject();
  const count = useRef(0);
  const uploadedFilesRef = useRef({
    selectedSampleFiles,
    sourceFileResponseList
  });
  const { getNotificationPermission } = useDesktopNotifications();

  useEffect(() => {
    uploadedFilesRef.current = {
      selectedSampleFiles: selectedSampleFiles || [],
      sourceFileResponseList: sourceFileResponseList || {}
    };
  }, [selectedSampleFiles, sourceFileResponseList]);

  const postFileSelect = () => {
    console.log(projectId);
    if (entryPoint === UPLOAD_FILES_ENTRY_POINT.CREATE_PROJECT) {
      if (
        !isLoading &&
        currentStep === CREATE_PROJECT_STEPS.UPLOAD_FILES.int &&
        count.current === 0
      ) {
        count.current++;
        handleCurrentStep();
      }
    } else if (entryPoint === UPLOAD_FILES_ENTRY_POINT.PROJECT_DETAILS) {
      getNotificationPermission();
      dispatch(setAddMoreFilesModal(false));
    }
  };

  const _setIsLoading = (val: boolean) => {
    setIsLoading(val);
  };

  const handleUploadFiles = (projectId: string) => {
    uppyFactory.setProjectId(projectId);
    const allFilesHaveDuration = checkIfAllFilesHaveDuration();
    if (allFilesHaveDuration && uppyFactory.uppyObj) {
      uppyFactory.uppyObj.upload().then((result) => {
        console.debug("Upload result:", result);
        shouldUpload.current = false;
      });
    } else {
      shouldUpload.current = true;
    }
  };

  const handleOnFileAdded = (files: any) => {
    if (!files.length || isCreatingProject.current) {
      return;
    }
    if (projectId) {
      initializeUppyWithProjectId();
      // handleSampleFileUpload(projectId, uploadedFilesRef.current);
      handleUploadFiles(projectId);
      postFileSelect();
    } else if (!isProjectCreated.current) {
      // No project created yet
      _setIsLoading(true);
      isCreatingProject.current = true;
      handleCreateProject()
        .then((res) => res.data.responseData)
        .then((project) => {
          isProjectCreated.current = true;
          isCreatingProject.current = false;
          console.debug("[INFO] Project Created", project);
          if (project) {
            const projectId = project.projectId;
            initializeUppyWithProjectId();
            dispatch(setProjectId(projectId));
            handleSampleFileUpload(projectId, uploadedFilesRef.current);
            handleUploadFiles(projectId);
            // postFileSelect; different for this flow
            if (
              [
                PROJECT_FLOW_TYPES.NON_ICP_NEW,
                PROJECT_FLOW_TYPES.ENTERPRISE
              ].includes(currentFlowType)
            ) {
              dispatch(
                setCurrentStep(CREATE_PROJECT_STEPS.SELECT_LANGUAGE.int)
              );
            } else {
              dispatch(setCurrentStep(CREATE_PROJECT_STEPS.REQUIREMENTS.int));
            }
            _setIsLoading(false);
            // END postFileSelect
          }
        })
        .catch((error) => {
          isCreatingProject.current = false;
          console.error("[ERR] Project Creation Failed", error);
          _setIsLoading(false);
        });
    }
  };

  const { initializeUppyWithProjectId } = useUppy({
    // postFileSelect
  });

  function checkIfAllFilesHaveDuration() {
    const files = uppyFactory.uppyObj?.getFiles();
    const allReady = files?.every((file) => file.meta.duration !== undefined);

    console.debug("[INFO] All files have duration", allReady);
    return allReady;
  }

  useEffect(() => {
    const onFilesAdded = (files: UppyFile<any, any>[]) => {
      console.log("[INFO] Files Added", files);

      files.forEach((f) => {
        const audio = document.createElement("audio");
        audio.src = URL.createObjectURL(f.data);

        audio.addEventListener("loadedmetadata", () => {
          const duration = audio.duration;
          const fileSizeInGB = (f?.size ?? 0) / (1024 * 1024 * 1024);

          const RESTRICTION_ON_DURATION = 1 * 60 * 90; // 90 mins
          let isUnsupportedFile = false;
          if (uppyFactory.uppyObj) {
            if (duration > RESTRICTION_ON_DURATION) {
              isUnsupportedFile = true;
            }

            uppyFactory.uppyObj.setFileMeta(f.id, {
              duration,
              fileSizeInGB,
              isUnsupportedFile,
              error:
                fileSizeInGB > 1
                  ? "File size is too long (> 1GB)"
                  : duration > RESTRICTION_ON_DURATION
                  ? "Max supported duration is 90 mins"
                  : ""
            });

            if (duration > RESTRICTION_ON_DURATION) {
              uppyFactory.uppyObj.setFileState(f.id, {
                progress: {
                  ...f.progress,
                  uploadComplete: true
                }
              });
            }

            const allFilesAreReady = checkIfAllFilesHaveDuration();
            if (shouldUpload.current && allFilesAreReady) {
              uppyFactory.uppyObj.upload().then((result) => {
                console.log("Upload result:", result);
                shouldUpload.current = false;
              });
            }
          }
        });
      });

      handleOnFileAdded(files);
    };
    uppyFactory.uppyObj?.on("files-added", onFilesAdded);

    return () => {
      uppyFactory.uppyObj?.off("files-added", onFilesAdded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (uppyFactory.uppyObj) {
      const _currentEl = uppyFactory.uppyObj.getPlugin(PLUGIN_ID);
      if (_currentEl) {
        return;
      }

      uppyFactory.uppyObj?.use(DragDrop, {
        id: PLUGIN_ID,
        target: "#custom-uppy-drag-n-drop"
      });
    }

    /**
     * IMP: On each unmount of this component, uppy will still contain the plugin but the DOM element will not be present.
     * So the next time this component mounts, uppy will not re-install the plugin as it was already installed in the first time.
     *
     * So it's necessary to remove the plugin on unmount
     */

    return () => {
      try {
        uppyFactory.removePlugin(PLUGIN_ID);
        count.current = 0;
      } catch (error) {
        console.debug("[ERR] Removing Plugin", error);
      }
    };
  }, []);

  useEffect(() => {
    if (projectId) {
      uppyFactory.setProjectId(projectId);
    }
  }, [projectId]);

  const handleKeyDown = () => {
    if (uppyRef.current) {
      const _button = uppyRef.current.querySelector("button");
      if (_button) {
        _button.click();
      }
    }
  };

  return (
    <Box justifySelf={"center"} height="auto" display={"flex"} width={"100%"}>
      <Stack direction={"row"} spacing={2} width={"100%"}>
        {/* Drag And Drop */}
        <Stack
          {...blockStyles}
          sx={{
            bgcolor: "background.default",
            border: "1px dashed",
            borderColor: "elevation.outlinedBorder",
            width: "50%",
            minHeight: 200,
            boxSizing: "content-box",
            ["&:hover"]: {
              boxShadow: "none"
            },
            [`& #custom-uppy-drag-n-drop:focus-visible + #file-drag-n-drop`]: {
              outline: `2px solid`,
              outlineColor: MISC_COLORS.FOCUS_COLOR,
              outlineOffset: 2
            }
          }}
          id="file-drag-n-drop-wrapper"
        >
          <Typography variant="subtitle1">{title}</Typography>
          <Stack gap={2} id="file-drag-n-drop">
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                startIcon={<UploadRounded />}
                onKeyDown={accessibilityUtils.handleOnKeyDown(handleKeyDown)}
                onClick={(e) => {
                  e.stopPropagation();
                  handleKeyDown();
                }}
                sx={{
                  width: "auto",
                  mx: "auto"
                }}
              >
                Select files
              </Button>
            </Box>
            <Typography
              variant="caption"
              textAlign={"center"}
              color={"text.secondary"}
            >
              Supported formats: .mp4, .mov, .avi, .mkv, .mp3, .wav, .aac
              <br />
              Max supported duration is 90 mins. Files should contain only one
              language.
            </Typography>
          </Stack>
          <div
            id="custom-uppy-drag-n-drop"
            ref={uppyRef}
            aria-label="drag files to add to project"
          ></div>
        </Stack>
        {/* [END] Drag And Drop */}
      </Stack>
    </Box>
  );
};
